import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useDocuments } from "@/hooks/useDocuments";
import { useDocumentFolders } from "@/hooks/useDocumentFolders";
import { DocumentList } from "@/components/ui/document-list";
// Removed outer dialog usage for upload; DocumentUpload provides its own dialog
import { DocumentUpload } from "@/components/ui/document-upload";
// Removed folder dropdown UI
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { DocumentFolderTiles } from "@/components/ui/document-folder-tiles";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Folder as FolderIcon } from "lucide-react";

export default function Documents() {
  const { toast } = useToast();
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/user"] });
  const clientId = user?.clientId as number | undefined;
  // Removed outer upload dialog state to avoid double dialog
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderVisibleToEmployees, setNewFolderVisibleToEmployees] =
    useState(false);

  // Folder navigation state (hierarchical)
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [folderPath, setFolderPath] = useState<
    Array<{ id: number | null; name: string }>
  >([{ id: null, name: "Documents" }]);

  // Removed folder dropdown selection state (navigation handled by tiles & breadcrumb)

  // Documents scoped by client
  const { documents, loading, error, refreshDocuments } = useDocuments({
    autoRefresh: true,
    ownerType: "client",
    clientId,
  // Show only documents with no folder when at root (currentFolderId null)
  folderId: currentFolderId && currentFolderId > 0 ? currentFolderId : undefined,
  unfiled: currentFolderId === null ? true : undefined,
  });

  // Folders for the current parent folder
  const {
    folders,
    createFolder,
    updateFolder,
    refreshFolders,
  } = useDocumentFolders({
    ownerType: "client",
    clientId,
    parentId: currentFolderId, // null => root folders
  });

  // Build breadcrumb display name (skip root title replacement later)
  const breadcrumb = useMemo(
    () =>
      folderPath
        .filter((p) => !(p.id === null && p.name === "Documents"))
        .map((p) => p.name)
        .join(" -> "),
    [folderPath]
  );

  const moveToFolder = async (documentId: number, folderId: number | null) => {
    const res = await fetch(`/api/documents/${documentId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ folderId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to move");
    }
    await refreshDocuments();
  };

  const enterFolder = (folder: { id: number; name: string }) => {
    setCurrentFolderId(folder.id);
    setFolderPath((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const goToBreadcrumb = (index: number) => {
    // index in folderPath
    const target = folderPath[index];
    setFolderPath(folderPath.slice(0, index + 1));
    setCurrentFolderId(target.id);
  };

  // When client changes, reset path
  useEffect(() => {
    setCurrentFolderId(null);
    setFolderPath([{ id: null, name: "Documents" }]);
  }, [clientId]);

  return (
    <div className="space-y-6">
  <div className="flex items-center justify-between">
  {breadcrumb ? (
          <nav className="flex flex-wrap items-center gap-1 text-sm">
            {folderPath.map((p, i) => {
              const isLast = i === folderPath.length - 1;
              return (
                <span key={i} className="flex items-center">
                  <button
                    disabled={isLast}
                    onClick={() => !isLast && goToBreadcrumb(i)}
                    className={`inline-flex items-center gap-1 ${
                      isLast
                        ? "font-semibold text-gray-900 dark:text-gray-100"
                        : "text-blue-600 hover:underline"
                    }`}
                  >
                    {i === 0 ? <FolderIcon className="h-4 w-4" /> : null}
                    {p.name}
                  </button>
                  {!isLast && (
                    <ChevronRight className="mx-1 h-4 w-4 text-gray-400" />
                  )}
                </span>
              );
            })}
          </nav>
        ) : (
          <h1 className="text-3xl font-bold">Documents</h1>
        )}
        <div className="flex items-center gap-2">
          {creatingFolder ? (
            <div className="flex items-center gap-2">
              <Input
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="h-8"
              />
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <Checkbox
                  id="new-folder-visible-to-employees"
                  checked={newFolderVisibleToEmployees}
                  onCheckedChange={(checked) =>
                    setNewFolderVisibleToEmployees(checked === true)
                  }
                />
                <Label
                  htmlFor="new-folder-visible-to-employees"
                  className="text-xs font-normal"
                >
                  Visible to employees
                </Label>
              </div>
              <Button
                size="sm"
                onClick={async () => {
                  if (!newFolderName.trim()) return;
                  try {
                    await createFolder(
                      newFolderName.trim(),
                      null,
                      newFolderVisibleToEmployees
                    );
                    setNewFolderName("");
                    setNewFolderVisibleToEmployees(false);
                    setCreatingFolder(false);
                    await refreshFolders();
                    toast({ title: "Folder created" , variant: "success"});
                  } catch (e: any) {
                    toast({
                      title: "Create failed",
                      description: e.message,
                      variant: "destructive",
                    });
                  }
                }}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setCreatingFolder(false);
                  setNewFolderName("");
                  setNewFolderVisibleToEmployees(false);
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCreatingFolder(true)}
            >
              New Folder
            </Button>
          )}
          <DocumentUpload
            trigger={
              <Button size="sm">
                <Upload className="h-4 w-4 mr-2" /> Upload Document
              </Button>
            }
            ownerType="client"
            clientId={clientId}
            folderId={
              currentFolderId && currentFolderId > 0
                ? currentFolderId
                : undefined
            }
            // `folders` holds only children of the current folder, so prepend
            // the current folder itself or the dialog's default id would be
            // missing from the options
            folders={
              currentFolderId
                ? [
                    {
                      id: currentFolderId,
                      name:
                        folderPath[folderPath.length - 1]?.name ??
                        "Current folder",
                    },
                    ...folders,
                  ]
                : folders
            }
            onUploadSuccess={() => {
              refreshDocuments();
              toast({
                title: "Document uploaded",
                description: "Your document has been uploaded successfully.",
                variant: "success",
              });
            }}
          />
        </div>
      </div>

      {/* Folder tiles */}
      <DocumentFolderTiles
        folders={folders}
        onOpenFolder={(f) => enterFolder({ id: f.id, name: f.name })}
        onToggleEmployeeVisibility={async (folderId, visible) => {
          try {
            await updateFolder(folderId, { visibleToEmployees: visible });
            toast({
              title: visible
                ? "Folder shared with employees"
                : "Folder hidden from employees",
              variant: "success",
            });
          } catch (e: any) {
            toast({
              title: "Update failed",
              description: e.message,
              variant: "destructive",
            });
          }
        }}
        onRenameFolder={async (folderId, newName) => {
          try {
            const res = await fetch(`/api/document-folders/${folderId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ name: newName })
            });
            if (!res.ok) throw new Error('Rename failed');
            await refreshFolders();
            toast({ title: 'Folder renamed' });
            // Update breadcrumb if renaming current path element
            setFolderPath((prev) => prev.map(p => p.id === folderId ? { ...p, name: newName } : p));
          } catch (e: any) {
            toast({ title: 'Rename failed', description: e.message, variant: 'destructive' });
          }
        }}
        onDeleteFolder={async (folderId) => {
          try {
            const res = await fetch(`/api/document-folders/${folderId}?recursive=true`, {
              method: 'DELETE',
              credentials: 'include'
            });
            if (!res.ok) throw new Error('Delete failed');
            await refreshFolders();
            // If deleted folder is in breadcrumb path, trim path
            setFolderPath((prev) => {
              const idx = prev.findIndex(p => p.id === folderId);
              if (idx === -1) return prev;
              const newPath = prev.slice(0, idx); // go to parent of deleted
              const last = newPath[newPath.length - 1];
              setCurrentFolderId(last ? last.id : null);
              return newPath.length ? newPath : [{ id: null, name: 'Documents' }];
            });
            toast({ title: 'Folder deleted' , variant: "success"});
            // Refresh documents (some may have been in subtree)
            refreshDocuments();
          } catch (e: any) {
            toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
          }
        }}
      />

  {/* Folder dropdown removed; folder creation moved to header */}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800 text-sm">
            Error loading documents: {error}
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <DocumentList
          documents={documents as any}
          onDocumentUpdate={refreshDocuments}
          showActions={true}
          showEmployeeVisibility
          folders={folders}
          onMoveToFolder={moveToFolder}
          {...(currentFolderId === null && folders.length > 0
            ? {
                emptyStateTitle: "No unfiled documents",
                emptyStateDescription:
                  "Your documents are organized in the folders above. Open a folder to see them, or upload a new document.",
              }
            : {})}
        />
      )}

  {/* Upload dialog handled internally by DocumentUpload */}
    </div>
  );
}